/**
 * Full Time Jobs API Service
 * Combines JSearch and Google Jobs APIs to fetch full-time positions
 */

import { searchFreelanceJobs as searchJSearch, isAPIConfigured as isJSearchConfigured } from './jobSearchAPI';
import { searchGoogleJobs, isAPIConfigured as isGoogleConfigured } from './googleJobsAPI';

/**
 * Search for full-time jobs from all available APIs
 * @param {Object} params - Search parameters
 * @returns {Promise<Object>} Combined results from all APIs
 */
export async function searchFullTimeJobs({
  query = 'engineering jobs',
  location = 'United Kingdom',
  page = 1,
  remote_jobs_only = false,
  userProfile = null, // User profile data
}) {
  console.log('🔍 Searching Full Time Jobs...');
  
  // Build query based on user profile
  let searchQuery = query;
  if (userProfile) {
    const jobTitle = userProfile.jobTitle || userProfile.profession || userProfile.occupation;
    const skills = userProfile.skills || [];
    
    if (jobTitle) {
      searchQuery = jobTitle;
      console.log(`👤 Using user profile: ${jobTitle}`);
    } else if (skills.length > 0) {
      searchQuery = skills[0];
      console.log(`👤 Using user skills: ${skills[0]}`);
    }
  }
  
  const results = {
    success: true,
    jobs: [],
    sources: {
      jsearch: { enabled: false, count: 0, error: null },
      google: { enabled: false, count: 0, error: null },
    },
    total: 0,
  };

  // Fetch from JSearch API
  if (isJSearchConfigured()) {
    results.sources.jsearch.enabled = true;
    try {
      console.log('📡 Fetching from JSearch API...');
      const jsearchResult = await searchJSearch({
        query: searchQuery,
        location,
        page,
        num_pages: 1,
        remote_jobs_only,
        employment_types: 'FULLTIME', // Only full-time jobs
      });

      if (jsearchResult.success && jsearchResult.jobs) {
        results.jobs.push(...jsearchResult.jobs);
        results.sources.jsearch.count = jsearchResult.jobs.length;
        console.log(`✅ JSearch: ${jsearchResult.jobs.length} full-time jobs`);
      }
    } catch (error) {
      console.error('❌ JSearch API Error:', error);
      results.sources.jsearch.error = error.message;
    }
  }

  // Fetch from Google Jobs API
  if (isGoogleConfigured()) {
    results.sources.google.enabled = true;
    try {
      console.log('📡 Fetching from Google Jobs API...');
      const googleResult = await searchGoogleJobs({
        query: `${searchQuery} full time`,
        location,
        page,
      });

      if (googleResult.success && googleResult.jobs) {
        // Mark as Google source and filter duplicates
        const googleJobs = googleResult.jobs
          .map(job => ({ ...job, apiSource: 'Google Jobs' }))
          .filter(googleJob => {
            // Check if job already exists from JSearch
            return !results.jobs.some(existingJob => 
              isSimilarJob(existingJob, googleJob)
            );
          });

        results.jobs.push(...googleJobs);
        results.sources.google.count = googleJobs.length;
        console.log(`✅ Google Jobs: ${googleJobs.length} full-time jobs`);
      }
    } catch (error) {
      console.error('❌ Google Jobs API Error:', error);
      results.sources.google.error = error.message;
    }
  }

  // Sort by match score and posted date
  results.jobs.sort((a, b) => {
    // Prioritize featured jobs
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    
    // Then by match score
    if (b.matchScore !== a.matchScore) {
      return (b.matchScore || 0) - (a.matchScore || 0);
    }
    
    // Then by posted date (most recent first)
    return 0;
  });

  results.total = results.jobs.length;
  console.log(`📊 Total Full Time Jobs: ${results.total}`);

  return results;
}

/**
 * Check if two jobs are similar (to remove duplicates)
 */
function isSimilarJob(job1, job2) {
  const title1 = (job1.title || '').toLowerCase().trim();
  const title2 = (job2.title || '').toLowerCase().trim();
  const company1 = (job1.company || job1.client || '').toLowerCase().trim();
  const company2 = (job2.company || job2.client || '').toLowerCase().trim();

  // Same title and company = duplicate
  if (title1 === title2 && company1 === company2) {
    return true;
  }

  // Very similar titles (>80% match) and same company
  if (company1 === company2 && calculateSimilarity(title1, title2) > 0.8) {
    return true;
  }

  return false;
}

/**
 * Calculate string similarity (0-1)
 */
function calculateSimilarity(str1, str2) {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;
  
  if (longer.length === 0) return 1.0;
  
  const editDistance = getEditDistance(longer, shorter);
  return (longer.length - editDistance) / longer.length;
}

/**
 * Calculate edit distance between two strings
 */
function getEditDistance(str1, str2) {
  const costs = [];
  for (let i = 0; i <= str1.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= str2.length; j++) {
      if (i === 0) {
        costs[j] = j;
      } else if (j > 0) {
        let newValue = costs[j - 1];
        if (str1.charAt(i - 1) !== str2.charAt(j - 1)) {
          newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
        }
        costs[j - 1] = lastValue;
        lastValue = newValue;
      }
    }
    if (i > 0) costs[str2.length] = lastValue;
  }
  return costs[str2.length];
}

/**
 * Get API configuration status
 */
export function getAPIStatus() {
  return {
    jsearch: isJSearchConfigured(),
    google: isGoogleConfigured(),
  };
}

/**
 * Get list of configured APIs
 */
export function getConfiguredAPIs() {
  const apis = [];
  if (isJSearchConfigured()) apis.push('JSearch');
  if (isGoogleConfigured()) apis.push('Google Jobs');
  
  return {
    list: apis,
    total: apis.length,
  };
}

export default {
  searchFullTimeJobs,
  getAPIStatus,
  getConfiguredAPIs,
};

